/**
 * Cold-path Business Associate Agreement text rendered in-app (DRAFT until counsel approves).
 * Not legal advice. Counsel must review before removing DRAFT watermark.
 */

import {
  MD3C_LEGAL_NAME,
  MD3C_STATE_OF_FORMATION,
  MD3C_PRINCIPAL_ADDRESS,
  MD3C_BAA_GOVERNING_LAW,
  MD3C_BAA_MASTER_FRAMEWORK_DATE,
  MD3C_BAA_SIGNATORY_NAME,
  MD3C_BAA_SIGNATORY_TITLE,
} from '../constants/coldPathBaa';

/**
 * @param {Object} p
 * @param {string} p.enrolleeLegalName
 * @param {string} p.enrolleeAddress - professional address or fallback line
 * @param {string} p.effectiveDateDisplay - human-readable local date/time of acceptance
 * @param {string} p.termsVersion
 * @param {string} p.privacyVersion
 * @returns {string}
 */
export function buildColdPathBaaText({
  enrolleeLegalName,
  enrolleeAddress,
  effectiveDateDisplay,
  termsVersion,
  privacyVersion,
}) {
  const ceName = (enrolleeLegalName || '').trim() || '[Enrollee legal name]';
  const ceAddr = (enrolleeAddress || '').trim() || 'As associated with the Covered Entity account in the Services.';

  return `BUSINESS ASSOCIATE AGREEMENT

This Business Associate Agreement ("Agreement") is entered into as of ${effectiveDateDisplay} ("Effective Date"), by and between:

${MD3C_LEGAL_NAME}, a ${MD3C_STATE_OF_FORMATION} limited liability company, with its principal place of business at ${MD3C_PRINCIPAL_ADDRESS} ("Business Associate"),

and

${ceName}, with a professional address at ${ceAddr} ("Covered Entity").

Covered Entity and Business Associate are each a "Party" and together the "Parties."

RECITALS

A. Covered Entity is a "covered entity" or otherwise subject to the privacy and security requirements of the Health Insurance Portability and Accountability Act of 1996, as amended, and its implementing regulations, including the Privacy, Security, Breach Notification, and Enforcement Rules at 45 C.F.R. Parts 160 and 164 (collectively, "HIPAA").

B. Business Associate provides certain services to or on behalf of Covered Entity that may involve the use or disclosure of "protected health information" ("PHI") as defined by HIPAA.

C. The Parties wish to satisfy HIPAA's requirements for a written business associate agreement.

D. Business Associate has adopted a master counterpart to this form of Agreement dated ${MD3C_BAA_MASTER_FRAMEWORK_DATE}. Covered Entity's electronic acceptance of this Agreement constitutes acceptance of terms substantially identical to that master framework, and Business Associate's obligations are satisfied by that master execution.

1. Definitions

Terms used but not otherwise defined have the meanings set forth in HIPAA. "Services" means the software, platform, and related services provided by Business Associate to Covered Entity.

2. Underlying Agreement (non-HIPAA terms)

Non-HIPAA terms of the relationship are governed by the HealthDesk / MSI3 online Terms of Use and Privacy Policy, identified by version IDs ${termsVersion} (Terms of Use) and ${privacyVersion} (Privacy Policy) respectively, accessible within the application at /terms-of-use and /privacy-policy as of the Effective Date (together, the "Underlying Agreement"). If there is a conflict between this Agreement and the Underlying Agreement regarding PHI, this Agreement controls.

3. Permitted uses and disclosures by Business Associate

3.1 Business Associate may use and disclose PHI only: (a) as necessary to perform the Services for or on behalf of Covered Entity; (b) as required by law; (c) for the proper management and administration of Business Associate or to carry out its legal responsibilities, consistent with 45 C.F.R. § 164.504(e)(4); (d) to report violations of law to appropriate federal or state authorities, consistent with 45 C.F.R. § 164.502(j)(1); and (e) as otherwise permitted by HIPAA.

3.2 Business Associate shall not use or disclose PHI in a manner that would violate the Privacy Rule if done by Covered Entity, except to the extent HIPAA permits Business Associate to do so.

4. Adequate assurances

4.1 Safeguards. Business Associate shall implement appropriate administrative, physical, and technical safeguards to prevent use or disclosure of PHI other than as permitted by this Agreement and shall comply with the Security Rule with respect to electronic PHI, as applicable.

4.2 Subcontractors. Business Associate shall ensure that any agent or subcontractor that creates, receives, maintains, or transmits PHI on behalf of Business Associate agrees in writing to the same restrictions and conditions that apply to Business Associate under this Agreement with respect to such PHI.

4.3 Minimum necessary. To the extent required by HIPAA, Business Associate shall use or disclose, and request from Covered Entity, only the minimum PHI necessary to perform the Services.

5. Obligations of Covered Entity

Covered Entity shall: (a) notify Business Associate of any limitation(s) in its notice of privacy practices or consent/restriction that may affect Business Associate's use or disclosure of PHI; (b) notify Business Associate of changes in, or revocation of, permission by an individual to use or disclose PHI; and (c) not request Business Associate to use or disclose PHI in a manner that would violate HIPAA.

6. Individual rights

To the extent Business Associate maintains PHI in a designated record set for Covered Entity, Business Associate shall, within a reasonable time and as directed by Covered Entity, make PHI available for access and amendment and provide an accounting of disclosures, as required by HIPAA. If an individual submits a request directly to Business Associate, Business Associate shall forward the request to Covered Entity within a reasonable time.

7. Availability of books and records

Business Associate agrees to make its internal practices, books, and records relating to the use and disclosure of PHI available to the Secretary of the U.S. Department of Health and Human Services for purposes of determining compliance with HIPAA, to the extent required by 45 C.F.R. § 164.504(e)(2)(ii)(H).

8. Breach notification

Business Associate shall notify Covered Entity without unreasonable delay after Business Associate discovers a breach of unsecured PHI as defined by HIPAA. The notice shall include, to the extent known, the information required under 45 C.F.R. § 164.410.

9. Return or destruction

Upon termination of the Underlying Agreement or this Agreement for any reason, Business Associate shall, at the option of Covered Entity, return or destroy all PHI received from Covered Entity or created or received on behalf of Covered Entity, including copies, that Business Associate still maintains in any form, and retain no copies except as required by law or for backups securely isolated and subject to continued protection. If return or destruction is infeasible, Business Associate shall extend the protections of this Agreement to such PHI and limit further uses to those necessary for continued protection.

10. Term and termination

10.1 This Agreement is effective as of the Effective Date and terminates when the Underlying Agreement terminates or when all PHI is returned or destroyed, subject to Section 9.

10.2 Upon Covered Entity's knowledge of a material breach by Business Associate, Covered Entity may terminate this Agreement and the Underlying Agreement if cure is not possible or Business Associate does not cure within a reasonable period specified by Covered Entity.

10.3 Business Associate shall report to Covered Entity any use or disclosure of PHI not permitted by this Agreement of which Business Associate becomes aware.

11. Miscellaneous

11.1 Interpretation. Any ambiguity in this Agreement shall be resolved to permit the Parties to comply with HIPAA.

11.2 Survival. Sections intended to survive termination shall survive.

11.3 Amendment. This Agreement may be amended only in a writing signed by both Parties, except that the Parties shall amend this Agreement as necessary for either Party to comply with HIPAA.

11.4 Counterparts / electronic signature. This Agreement may be executed in counterparts, including by electronic means acceptable to the Parties.

11.5 Governing law. This Agreement shall be governed by the laws of ${MD3C_BAA_GOVERNING_LAW}, without regard to conflict-of-law principles, except to the extent preempted by federal law.

SIGNATURES — MASTER FRAMEWORK (BUSINESS ASSOCIATE)

${MD3C_LEGAL_NAME} has executed a master counterpart to this Agreement dated ${MD3C_BAA_MASTER_FRAMEWORK_DATE}.

By: _________________________________
Name: ${MD3C_BAA_SIGNATORY_NAME}
Title: ${MD3C_BAA_SIGNATORY_TITLE}
Date: ${MD3C_BAA_MASTER_FRAMEWORK_DATE}

ELECTRONIC ACCEPTANCE (COVERED ENTITY)

By accepting in the HealthDesk application on ${effectiveDateDisplay}, ${ceName} ("Covered Entity") agrees to the terms of this Agreement, represents that the legal name and address shown above are accurate, and agrees that the Underlying Agreement (Terms version ${termsVersion}, Privacy version ${privacyVersion}) applies as described in Section 2.

Covered Entity (electronic acceptance): ${ceName}
Date/time of acceptance (device local): ${effectiveDateDisplay}
`;
}
